import { useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { DOCUMENT_CHECKLIST_FIELDS } from "../constants/documentChecklistFields";
import { invalidateEmployeeCache } from "../utils/employeeCache";

const API_URL = process.env.REACT_APP_BASE_URL;

// Same field list EditEmployeeModal's "Documents Checklist" section uses —
// keeps this quick-view checklist and the full edit form checklist in sync.
const defaultDocumentChecklist = () =>
  DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
    acc[field.name] = false;
    return acc;
  }, {});

/**
 * 📄 useEmployeeDocuments Hook
 * يدير نظام المستندات والشهادات للموظفين
 */
export function useEmployeeDocuments() {
  // ============== MODAL STATES ==============
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);

  // ============== SELECTED EMPLOYEE & DOCUMENTS ==============
  const [selectedEmployeeDocuments, setSelectedEmployeeDocuments] = useState(null);

  // ============== LOADING & SAVING STATES ==============
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [savingDocument, setSavingDocument] = useState(false);

  // ============== UNSAVED CHANGES TRACKING ==============
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ============== DOCUMENT CHECKLIST STATE ==============
  const [documentChecklist, setDocumentChecklist] = useState(defaultDocumentChecklist);

  // ============== HELPER FUNCTION: RESET DOCUMENTS STATE ==============
  const resetDocumentsState = () => {
    setShowDocumentsModal(false);
    setSelectedEmployeeDocuments(null);
    setLoadingDocuments(false);
    setSavingDocument(false);
    setHasUnsavedChanges(false);
    setDocumentChecklist(defaultDocumentChecklist());
  };

  // ============== HELPER FUNCTION: LOAD EMPLOYEE DOCUMENTS (with real API fetch) ==============
  const loadEmployeeDocuments = async (employee) => {
    try {
      setLoadingDocuments(true);
      setSelectedEmployeeDocuments(employee);
      setShowDocumentsModal(true);
      setHasUnsavedChanges(false);

      const empData = employee.fullEmployeeData || {};
      setDocumentChecklist(
        DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
          const raw = empData[field.name];
          // photosCount is stored as a count (e.g. 1), not a strict boolean —
          // every other field is a real boolean column.
          acc[field.name] =
            field.name === "photosCount" ? raw === 1 || raw === true : Boolean(raw);
          return acc;
        }, {}),
      );

      // Fetch real uploaded documents from API (use infinityCode, not id)
      const infinityCode = employee.infinityCode || employee.fullEmployeeData?.infinityCode || employee.id;
      const response = await apiFetch(
        `${API_URL}/api/documents/${infinityCode}`,
        { method: 'GET' }
      );
      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(responseData?.error || responseData?.message || 'Failed to fetch documents');
      }

      setSelectedEmployeeDocuments({
        ...employee,
        documents: Array.isArray(responseData) ? responseData : [],
      });
    } catch (err) {
      console.error("❌ Error loading employee documents:", err);
      setSelectedEmployeeDocuments((prev) => prev ? { ...prev, documents: [] } : prev);
    } finally {
      setLoadingDocuments(false);
    }
  };

  // ============== HELPER FUNCTION: UPDATE DOCUMENT CHECKBOX ==============
  const updateDocumentCheckbox = (documentField, value) => {
    setDocumentChecklist((prev) => ({
      ...prev,
      [documentField]: value,
    }));
    setHasUnsavedChanges(true);
  };

  // ============== HELPER FUNCTION: BUILD DOCUMENTS PAYLOAD ==============
  const buildDocumentsPayload = () =>
    DOCUMENT_CHECKLIST_FIELDS.reduce((acc, field) => {
      acc[field.name] = Boolean(documentChecklist[field.name]);
      return acc;
    }, {});

  // ============== SAVE DOCUMENTS (PUT like EmployeeTable.js) ==============
  const saveDocuments = async (employeeId, setEmployees) => {
    try {
      if (!employeeId) return false;
      setSavingDocument(true);

      const payload = buildDocumentsPayload();

      const response = await apiFetch(
        `${API_URL}/employees/${employeeId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(responseData?.message || responseData?.error || `Server error: ${response.status}`);
      }

      // Update local employees list if setter provided
      if (typeof setEmployees === 'function') {
        setEmployees((prev) =>
          prev.map((emp) =>
            emp.id === employeeId
              ? { ...emp, fullEmployeeData: { ...(emp.fullEmployeeData || {}), ...payload } }
              : emp
          )
        );
      }

      // The cached employee list no longer reflects this document update —
      // drop it so a remount within the TTL window doesn't revert it.
      invalidateEmployeeCache("employees:");

      // Update selectedEmployeeDocuments locally
      setSelectedEmployeeDocuments((prev) =>
        prev ? { ...prev, fullEmployeeData: { ...(prev.fullEmployeeData || {}), ...payload } } : prev
      );

      setHasUnsavedChanges(false);
      return true;
    } catch (err) {
      console.error('❌ Failed to save documents:', err);
      throw err;
    } finally {
      setSavingDocument(false);
    }
  };

  // ============== RETURN PUBLIC API ==============
  return {
    // Modal state
    showDocumentsModal,
    setShowDocumentsModal,

    // Employee & documents
    selectedEmployeeDocuments,
    setSelectedEmployeeDocuments,

    // Loading states
    loadingDocuments,
    setLoadingDocuments,
    savingDocument,
    setSavingDocument,

    // Unsaved changes
    hasUnsavedChanges,
    setHasUnsavedChanges,

    // Document checklist
    documentChecklist,
    setDocumentChecklist,

    // Helper methods
    resetDocumentsState,
    loadEmployeeDocuments,
    updateDocumentCheckbox,
    buildDocumentsPayload,
    saveDocuments,
  };
}
